<script setup lang="ts">
/**
 * Banner displayed when a new version of the app is available.
 * Shows a dismissible notification prompting the user to refresh.
 */

import { nextTick, ref } from 'vue'
import { useVersionCheck } from '@/composables/use-version-check'

const { showBanner, refreshApp, dismissBanner } = useVersionCheck()

const refreshing = ref(false)

function handleRefresh() {
  refreshing.value = true
  nextTick(() => {
    refreshApp()
  })
}
</script>

<template>
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 -translate-y-full"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 -translate-y-full"
  >
    <div
      v-if="showBanner"
      class="shrink-0 bg-stone-900 text-white px-4 py-3 mb-4 rounded-md shadow-sm"
    >
      <div class="flex items-center justify-between gap-4">
        <div class="flex items-center gap-3">
          <i class="pi pi-refresh text-lg"></i>
          <span class="text-sm font-medium">
            A new version is available. Refresh to get the latest features and improvements.
          </span>
        </div>

        <div class="flex items-center gap-2">
          <button
            type="button"
            class="inline-flex items-center justify-center gap-2 min-w-[7.5rem] px-4 py-1.5 text-sm font-medium bg-white text-stone-900 rounded-md hover:bg-stone-100 transition-colors disabled:opacity-80 disabled:pointer-events-none"
            :disabled="refreshing"
            :aria-busy="refreshing"
            @click="handleRefresh"
          >
            <i v-if="refreshing" class="pi pi-spin pi-spinner" aria-hidden="true"></i>
            {{ refreshing ? 'Refreshing...' : 'Refresh now' }}
          </button>
          <button
            class="p-1.5 text-stone-400 hover:text-white transition-colors"
            aria-label="Dismiss"
            @click="dismissBanner"
          >
            <i class="pi pi-times text-sm"></i>
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>
